import Vue from "vue";
import Vuex from "vuex";
import Repository from "../repositories/Repository";
import decode from "jwt-decode";
import router from "../router/index";

Vue.use(Vuex);

const loading = {
  namespaced: true,
  state: { loadingOverlay: false },
  mutations: {
    setLoadingOverlay(state, loadingOverlay) {
      state.loadingOverlay = loadingOverlay;
    },
    removeLoadingOverlay(state, loadingOverlay) {
      state.loadingOverlay = loadingOverlay;
    },
  },
  actions: {
    setLoadingOverlay({ commit }, loadingOverlay) {
      loadingOverlay = true;
      commit("setLoadingOverlay", loadingOverlay);
    },
    removeLoadingOverlay({ commit }, loadingOverlay) {
      loadingOverlay = false;
      commit("removeLoadingOverlay", loadingOverlay);
    },
  },
  getters: {
    loading: (state) => {
      return state.loading;
    },
  },
};

const snackbar = {
  namespaced: true,
  state: { snackbar: { showing: false, text: "", color: "" } },
  mutations: {
    setSnackbar(state, snackbar) {
      state.snackbar = snackbar;
    },
  },
  actions: {
    setSnackbar({ commit }, snackbar) {
      snackbar.showing = true;
      snackbar.color = snackbar.color || "success";
      commit("setSnackbar", snackbar);
    },
  },
  getters: {
    snackbar: (state) => {
      return state.snackbar;
    },
  },
};

const user = {
  namespaced: true,
  state: { user: { name: "", lastname: "" }, token: null, userDB: null },
  mutations: {
    setUser(state, payload) {
      state.user = payload;
    },
    setToken(state, token) {
      state.token = token;
    },
    setUserDB(state, User) {
      if (User) {
        state.userDB = User.id;
        state.name = User.name;
      } else {
        state.userDB = null;
      }
    },
  },
  actions: {
    async login({ commit }, payload) {
      const response = await Repository.post(`/users/login`, payload);
      return response;
    },
    async forgotPassword({ commit }, payload) {
      const response = await Repository.post(`/users/forgotPassword`, payload);
      return response;
    },
    async resetPassword({ commit }, payload) {
      const response = await Repository.post(
        `/users/resetpassword/${payload.token}`,
        payload
      );
      return response;
    },
    async getUser({ commit }, payload) {
      const response = await Repository.get(
        `users/user?_id=${this.state.user.userDB}`,
        {
          headers: { token: this.state.user.token },
        }
      );
      commit("setUser", response.data);
    },
    saveToken({ commit }, token) {
      commit("setToken", token);
      const User = decode(token);
      commit("setUserDB", User);
      localStorage.setItem("token", token);
    },
    exit({ commit }) {
      commit("setToken", null);
      commit("setUserDB", null);
      localStorage.removeItem("token");
      router.push({ name: "Login" });
    },
    autoLogin({ commit }) {
      let token = localStorage.getItem("token");
      if (token) {
        const User = decode(token);
        if (User.exp < Date.now() / 1000) {
          commit("setToken", null);
          commit("setUserDB", null);
          localStorage.removeItem("token");
          router.push({ name: "Login" });
        } else {
          commit("setToken", token);
          commit("setUserDB", User);
        }
      } else {
        router.push({ name: "Login" });
      }
    },
  },
  getters: {
    user: (state) => {
      return state.user;
    },
  },
};

const users = {
  namespaced: true,
  state: { users: [] },
  mutations: {
    setUsers(state, payload) {
      state.users = payload;
    },
  },
  actions: {
    async getUsers({ commit }, payload) {
      const response = await Repository.get(`/users`, {
        headers: { token: this.state.user.token },
      });
      commit("setUsers", response.data);
    },
  },
  getters: {
    users: (state) => {
      return state.users;
    },
  },
};

const sliders = {
  namespaced: true,
  state: { sliders: [] },
  mutations: {
    setSliders(state, payload) {
      state.sliders = payload;
    },
  },
  actions: {
    async createSlider({ commit }, payload) {
      const response = await Repository.post(`/sliders`, payload, {
        headers: {
          token: this.state.user.token,
          "Content-Type": "multipart/form-data",
        },
      });
      commit("setSliders", response.data);
      return response;
    },
    async createVideoSlider({ commit }, payload) {
      const response = await Repository.post(`/sliders/video`, payload, {
        headers: {
          token: this.state.user.token,
          "Content-Type": "multipart/form-data",
        },
      });
      commit("setSliders", response.data);
      return response;
    },
    async updateSlider({ commit }, payload) {
      const response = await Repository.put(`/sliders`, payload, {
        headers: {
          token: this.state.user.token,
          "Content-Type": "multipart/form-data",
        },
      });
      commit("setSliders", response.data);
      return response;
    },
    async setSlidersOrder({ commit }, payload) {
      const response = await Repository.post(
        `/sliders/setSlidersOrder`,
        payload,
        {
          headers: {
            token: this.state.user.token,
          },
        }
      );
      commit("setSliders", response.data);
      return response;
    },

    async createBackgroundImageSlider({ commit }, payload) {
      const response = await Repository.put(
        `/settings/createBackgroundImageSlider`,
        payload,
        {
          headers: {
            token: this.state.user.token,
          },
        }
      );
      return response;
    },
    async activeBackgroundVideo({ commit }, payload) {
      const response = await Repository.put(
        `/settings/activeBackgroundVideo`,
        payload,
        {
          headers: {
            token: this.state.user.token,
          },
        }
      );
      commit("setSliders", response.data);
      return response;
    },
    async deleteBackgroundVideo({ commit }, payload) {
      const response = await Repository.put(
        `/sliders/deleteBackgroundVideo`,
        payload,
        {
          headers: {
            token: this.state.user.token,
          },
        }
      );
      commit("setSliders", response.data);
      return response;
    },
    async deleteBackgroundVideoImage({ commit }, payload) {
      const response = await Repository.put(
        `/settings/deleteBackgroundVideoImage`,
        payload,
        {
          headers: {
            token: this.state.user.token,
          },
        }
      );
      commit("setSliders", response.data);
      return response;
    },
    async setSliderOverlayLevel({ commit }, payload) {
      const response = await Repository.put(
        `/settings/setSliderOverlayLevel`,
        payload,
        {
          headers: { token: this.state.user.token },
        }
      );
      return response;
    },
    async getSliders({ commit }, payload) {
      const response = await Repository.get(`/sliders`, {
        headers: { token: this.state.user.token },
      });
      commit("setSliders", response.data);
    },
    async deleteSlider({ commit }, params, payload) {
      const response = await Repository.delete(`/sliders`, {
        headers: { token: this.state.user.token },
        params: params,
      });
      return response;
    },
  },
  getters: {
    sliders: (state) => {
      return state.sliders;
    },
  },
};

const settings = {
  namespaced: true,
  state: { settings: [] },
  mutations: {
    setSettings(state, payload) {
      state.settings = payload;
    },
  },
  actions: {
    async getSettings({ commit }, payload) {
      const response = await Repository.get(`/settings`, {
        headers: { token: this.state.user.token },
      });
      commit("setSettings", response.data);
    },
    async updateColor({ commit }, payload) {
      const response = await Repository.put(`/settings/updateColor`, payload, {
        headers: { token: this.state.user.token },
      });
      return response;
    },
    async updateFont({ commit }, payload) {
      const response = await Repository.put(`/settings/updateFont`, payload, {
        headers: { token: this.state.user.token },
      });
      return response;
    },
    async setCompanyURL({ commit }, payload) {
      const response = await Repository.put(
        `/settings/updateCompanyURL`,
        payload,
        {
          headers: { token: this.state.user.token },
        }
      );
      return response;
    },
  },
  getters: {
    settings: (state) => {
      return state.settings;
    },
  },
};

const calltoactions = {
  namespaced: true,
  state: { calltoactions: [] },
  mutations: {
    setCallToActions(state, payload) {
      state.calltoactions = payload;
    },
  },
  actions: {
    async getCallToActions({ commit }, payload) {
      const response = await Repository.get(`/calltoactions`, {
        headers: { token: this.state.user.token },
      });
      commit("setCallToActions", response.data);
    },
    async createCallToAction({ commit }, payload) {
      const response = await Repository.post(`/calltoactions`, payload, {
        headers: { token: this.state.user.token },
      });
      return response;
    },
    async updateCallToAction({ commit }, payload) {
      const response = await Repository.put(`/calltoactions`, payload, {
        headers: { token: this.state.user.token },
      });
      return response;
    },
    async updateBackgroundImage({ commit }, payload) {
      const response = await Repository.put(
        `/calltoactions/background`,
        payload,
        {
          headers: {
            token: this.state.user.token,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response;
    },
    async deleteBackgroundImage({ commit }, payload) {
      const response = await Repository.put(
        `/calltoactions/deleteBackground`,
        payload,
        {
          headers: { token: this.state.user.token },
        }
      );
      return response;
    },
    async deleteCallToAction({ commit }, payload) {
      const response = await Repository.delete(`/calltoactions`, {
        params: payload,
        headers: { token: this.state.user.token },
      });
      return response;
    },
    async activateItem({ commit }, payload) {
      const response = await Repository.put(
        `/calltoactions/activate`,
        { _id: payload._id },
        {
          headers: { token: this.state.user.token },
        }
      );
      return response;
    },
    async desactivateItem({ commit }, payload) {
      const response = await Repository.put(
        `/calltoactions/desactivate`,
        { _id: payload._id },
        {
          headers: { token: this.state.user.token },
        }
      );
      return response;
    },
  },
  getters: {
    calltoactions: (state) => {
      return state.calltoactions;
    },
  },
};

const expenses = {
  namespaced: true,
  state: { expenses: [] },
  mutations: {
    setExpenses(state, payload) {
      state.expenses = payload;
    },
  },
  actions: {
    async getExpenses({ commit }, payload) {
      const response = await Repository.get(`/expenses`, {
        headers: { token: this.state.user.token },
      });
      commit("setExpenses", response.data);
    },
    async createExpense({ commit }, payload) {
      const response = await Repository.post(`/expenses`, payload, {
        headers: { token: this.state.user.token },
      });
      return response;
    },
    async updateExpense({ commit }, payload) {
      const response = await Repository.put(`/expenses`, payload, {
        headers: { token: this.state.user.token },
      });
      return response;
    },
    async deleteExpense({ commit }, payload) {
      const response = await Repository.delete(`/expenses`, {
        params: payload,
        headers: { token: this.state.user.token },
      });
      return response;
    },
    async activateItem({ commit }, payload) {
      const response = await Repository.put(
        `/expenses/activate`,
        { _id: payload._id },
        {
          headers: { token: this.state.user.token },
        }
      );
      return response;
    },
    async desactivateItem({ commit }, payload) {
      const response = await Repository.put(
        `/expenses/desactivate`,
        { _id: payload._id },
        {
          headers: { token: this.state.user.token },
        }
      );
      return response;
    },
  },
  getters: {
    expenses: (state) => {
      return state.expenses;
    },
  },
};

const menus = {
  namespaced: true,
  state: { menus: [] },
  mutations: {
    setMenus(state, payload) {
      state.menus = payload;
    },
  },
  actions: {
    async getMenus({ commit }, payload) {
      const response = await Repository.get(`/menus`, {
        headers: { token: this.state.user.token },
      });
      commit("setMenus", response.data);
    },
    async createMenu({ commit }, payload) {
      const response = await Repository.post(`/menus`, payload, {
        headers: { token: this.state.user.token },
      });
      return response;
    },
    async updateMenu({ commit }, payload) {
      const response = await Repository.put(`/menus`, payload, {
        headers: { token: this.state.user.token },
      });
      return response;
    },
    async deleteMenu({ commit }, payload) {
      const response = await Repository.delete(`/menus`, {
        params: payload,
        headers: { token: this.state.user.token },
      });
      return response;
    },
    async activateItem({ commit }, payload) {
      const response = await Repository.put(
        `/menus/activate`,
        { _id: payload._id },
        {
          headers: { token: this.state.user.token },
        }
      );
      return response;
    },
    async desactivateItem({ commit }, payload) {
      const response = await Repository.put(
        `/menus/desactivate`,
        { _id: payload._id },
        {
          headers: { token: this.state.user.token },
        }
      );
      return response;
    },
    async saveNewOrder({ commit }, payload) {
      const response = await Repository.post(`/menus/saveNewOrder`, payload, {
        headers: { token: this.state.user.token },
      });
      return response;
    },
  },
  getters: {
    menus: (state) => {
      return state.menus;
    },
  },
};

export default new Vuex.Store({
  state: {},
  mutations: {},
  actions: {},
  modules: {
    expenses: expenses,
    menus: menus,
    calltoactions: calltoactions,
    settings: settings,
    sliders: sliders,
    user: user,
    users: users,
    snackbar: snackbar,
    loading: loading,
  },
});
